import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Sparkles, Camera, Sun, Moon, Mountain, Building, 
  RefreshCw, Loader2, Copy, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useI18n } from '../context/I18nContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIPrediction = ({ latitude, longitude, weather, sunTimes, moonData, shotScore }) => {
  const { language } = useI18n();
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [photographyType, setPhotographyType] = useState('landscape');
  const [copied, setCopied] = useState(false);

  const photographyTypes = [
    { value: 'landscape', label: 'Landscape', icon: Mountain },
    { value: 'golden_hour', label: 'Golden Hour', icon: Sun },
    { value: 'blue_hour', label: 'Blue Hour', icon: Moon },
    { value: 'astro', label: 'Astrophotography', icon: Moon },
    { value: 'urban', label: 'Urban/Architecture', icon: Building },
  ];

  const fetchPrediction = async () => {
    setLoading(true);
    setPrediction(null);

    try {
      const response = await axios.post(`${API_URL}/ai-prediction`, {
        latitude,
        longitude,
        weather: weather?.current ? {
          cloud_cover: weather.current.cloud_cover,
          visibility: weather.current.visibility ? weather.current.visibility / 1000 : null,
          wind_speed: weather.current.wind_speed_10m,
          temperature: weather.current.temperature_2m
        } : null,
        sun_times: sunTimes,
        moon_data: moonData,
        shot_score: shotScore?.shot_score,
        photography_type: photographyType,
        language
      });

      setPrediction(response.data.prediction);
      toast.success('AI prediction generated!');
    } catch (error) {
      console.error('AI prediction error:', error);
      toast.error('Failed to generate prediction. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (prediction) {
      await navigator.clipboard.writeText(prediction);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const SelectedIcon = photographyTypes.find(t => t.value === photographyType)?.icon || Camera;

  return (
    <div className="space-y-4">
      <Card className="bg-gradient-to-br from-purple-500/20 via-pink-500/10 to-amber-500/20 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Shot Prediction
            <Badge className="ml-2 bg-purple-500/20 text-purple-300 border-purple-500/30">
              Powered by GPT
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-300">
            Get personalized photography recommendations based on current conditions.
          </p>

          {/* Photography Type Selection */}
          <div className="flex items-center gap-3">
            <Select value={photographyType} onValueChange={setPhotographyType}>
              <SelectTrigger className="w-[200px] bg-slate-800 border-white/10" data-testid="ai-photo-type">
                <SelectedIcon className="w-4 h-4 mr-2 text-purple-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                {photographyTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={fetchPrediction}
              disabled={loading}
              className="flex-1 bg-purple-500 hover:bg-purple-400 text-white font-bold"
              data-testid="generate-prediction-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get Recommendation
                </>
              )}
            </Button>
          </div>

          {/* Current Conditions Summary */}
          <div className="grid grid-cols-3 gap-2 p-3 rounded-lg bg-slate-800/50">
            <div className="text-center">
              <p className="text-xs text-slate-500">Shot Score</p>
              <p className={`font-mono text-lg ${
                (shotScore?.shot_score?.score || 0) >= 70 ? 'text-emerald-500' :
                (shotScore?.shot_score?.score || 0) >= 50 ? 'text-amber-500' : 'text-slate-400'
              }`}>
                {shotScore?.shot_score?.score || '--'}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">Clouds</p>
              <p className="font-mono text-lg text-white">
                {weather?.current?.cloud_cover || '--'}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">Moon</p>
              <p className="font-mono text-lg text-white">
                {moonData?.illumination || '--'}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Response */}
      {prediction && (
        <Card className="bg-slate-900/50 border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Camera className="w-4 h-4 text-amber-500" />
                Today's Recommendation
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyToClipboard}
                  className="text-slate-400 hover:text-white"
                  data-testid="copy-prediction-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchPrediction}
                  disabled={loading}
                  className="text-slate-400 hover:text-white"
                  data-testid="refresh-prediction-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-lg font-heading text-white mb-2">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-base font-heading text-amber-500 mt-4 mb-2">{children}</h2>,
                  p: ({ children }) => <p className="text-slate-300 mb-3 leading-relaxed">{children}</p>,
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                  ul: ({ children }) => <ul className="list-disc list-inside text-slate-300 space-y-1">{children}</ul>,
                  li: ({ children }) => <li className="text-slate-300">{children}</li>,
                }}
              >
                {prediction}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!prediction && !loading && (
        <div className="text-center py-8 text-slate-500">
          <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Click "Get Recommendation" to receive</p>
          <p className="text-sm">AI-powered photography advice</p>
        </div>
      )}
    </div>
  );
};

export default AIPrediction;
